import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Colors, FontSize, Spacing } from '../../../assets/GlobalStyle/Variables';

export type ContextMenuItem = {
  label: string;
  onClick: () => void;
};

type ContextMenuProps = {
  isOpen: boolean;
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
};

export const ContextMenu: FC<ContextMenuProps> = ({ isOpen, x, y, items, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <StyledContextMenu ref={menuRef} x={x} y={y}>
      {items.map((item, index) => (
        <StyledMenuItem
          key={index}
          onClick={() => {
            item.onClick();
            onClose();
          }}>
          {item.label}
        </StyledMenuItem>
      ))}
    </StyledContextMenu>
  );
};

const StyledContextMenu = styled.div<{ x: number; y: number }>`
  position: fixed;
  left: ${(props) => props.x}px;
  top: ${(props) => props.y}px;
  background-color: ${Colors.lightBackground};
  border: 1px solid ${Colors.lightGrey};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
`;

const StyledMenuItem = styled.div`
  padding: ${Spacing(3)} ${Spacing(4)};
  font-size: ${FontSize.normal};
  color: ${Colors.grey};
  cursor: pointer;
  border-bottom: 1px solid ${Colors.secondary};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${Colors.secondary};
    color: ${Colors.white};
  }
`;
